/**
 * Dawn: assets/product-info.js
 * PWG additions are intentionally small + scoped, so the rest of the theme keeps working seamlessly.
 *
 * PWG features included:
 * 1) Show/hide .nameplate-text when the Nameplate option is Yes
 *    - Keeps support for older variants containing "8x10 w/Nameplate"
 * 2) Disable Add to Cart (and dynamic checkout via a class hook) when option2 is "-----"
 *    - Works on initial page load + on variant changes
 * 3) If option2 and option3 are a size mismatch (5x7 vs 8x10), show red message where price is
 *    and disable Add to Cart (this happens when variant resolves to null OR when selections mismatch)
 *
 * NOTE: This file assumes you already added CSS for:
 *   .pwg-price-mismatch { color: red; font-weight: 600; }
 *   product-info.pwg-invalid-selection .shopify-payment-button { pointer-events:none; opacity:.5; }
 */

const PWG_NAMEPLATE_OPTION_NAME = 'Nameplate';
const PWG_NAMEPLATE_YES_VALUE = 'Yes';
const PWG_NAMEPLATE_LABEL_FRAGMENT = '8x10 w/Nameplate';
const PWG_INVALID_OPTION_LABEL = '-----';
const PWG_MAKE_SELECTION_BUTTON_TEXT = 'Make selection';
const PWG_PRICE_MISMATCH_TEXT = "Size and frame don't match";

if (!window.PWGSlideToggle) {
  window.PWGSlideToggle = {
    ensureShell(element) {
      if (!element) return null;

      let shell = element.parentElement;
      if (shell && shell.classList.contains('pwg-slide-shell')) return shell;

      shell = document.createElement('div');
      shell.className = 'pwg-slide-shell';
      shell.style.display = 'none';
      shell.style.overflow = 'hidden';
      shell.style.height = '0px';
      shell.style.width = '100%';
      shell.style.boxSizing = 'border-box';

      const computed = window.getComputedStyle(element);
      shell.dataset.originalDisplay = computed.display === 'none' ? '' : computed.display || '';
      shell.dataset.originalMarginTop = computed.marginTop || '';
      shell.dataset.originalMarginBottom = computed.marginBottom || '';

      element.style.marginTop = '0';
      element.style.marginBottom = '0';
      element.style.display = shell.dataset.originalDisplay || '';

      element.parentNode.insertBefore(shell, element);
      shell.appendChild(element);
      return shell;
    },

    getExpandedHeight(shell) {
      if (!shell) return 0;
      return Math.ceil(shell.scrollHeight);
    },

    setExpanded(element, expanded, options = {}) {
      if (!element) return;

      const settings = {
        animate: true,
        duration: 220,
        easing: 'cubic-bezier(0.2, 0.8, 0.2, 1)',
        onStart: null,
        onComplete: null,
        ...options,
      };

      const shell = this.ensureShell(element);
      if (!shell) return;

      if (typeof shell.__pwgStopAnimation === 'function') {
        shell.__pwgStopAnimation();
      }

      const complete = () => {
        shell.style.overflow = '';
        shell.style.transition = '';
        shell.style.willChange = '';
        shell.style.height = expanded ? '' : '0px';
        shell.style.display = expanded ? '' : 'none';
        shell.__pwgStopAnimation = null;

        if (typeof settings.onComplete === 'function') {
          settings.onComplete();
        }
      };

      if (!settings.animate) {
        if (typeof settings.onStart === 'function') settings.onStart();
        shell.style.display = expanded ? '' : 'none';
        shell.style.height = expanded ? '' : '0px';
        complete();
        return;
      }

      if (typeof settings.onStart === 'function') settings.onStart();

      shell.style.display = '';
      shell.style.overflow = 'hidden';
      shell.style.willChange = 'height';

      const startHeight = Math.ceil(shell.getBoundingClientRect().height);
      shell.style.height = `${startHeight}px`;
      shell.offsetHeight;

      let endHeight = 0;
      if (expanded) {
        shell.style.height = 'auto';
        endHeight = this.getExpandedHeight(shell);
        shell.style.height = `${startHeight}px`;
      } else {
        endHeight = 0;
      }

      if (startHeight === endHeight) {
        complete();
        return;
      }

      shell.style.transition = `height ${settings.duration}ms ${settings.easing}`;

      const onEnd = (event) => {
        if (event.target !== shell || event.propertyName !== 'height') return;
        shell.removeEventListener('transitionend', onEnd);
        complete();
      };

      shell.addEventListener('transitionend', onEnd);
      shell.__pwgStopAnimation = () => {
        shell.removeEventListener('transitionend', onEnd);
        const currentHeight = Math.ceil(shell.getBoundingClientRect().height);
        shell.style.transition = '';
        shell.style.height = `${currentHeight}px`;
      };

      requestAnimationFrame(() => {
        shell.style.height = `${endHeight}px`;
      });
    },
  };
}

if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      quantityInput = undefined;
      quantityForm = undefined;
      onVariantChangeUnsubscriber = undefined;
      cartUpdateUnsubscriber = undefined;
      abortController = undefined;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];

      constructor() {
        super();
        this.quantityInput = this.querySelector('.quantity__input');
      }

      connectedCallback() {
        this.initializeProductSwapUtility();

        this.onVariantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );

        this.initQuantityHandlers();

        // ---- PWG: apply rules on initial load (fixes "not disabled on first load") ----
        const initialVariant = this.getSelectedVariant(this);

        const getDomServerDisabled = () => {
          const btn =
            this.querySelector('.product-form__submit') || this.querySelector(`#ProductSubmitButton-${this.sectionId}`);
          return btn ? btn.hasAttribute('disabled') : null;
        };

        // Run immediately + once on next frame (covers cases where product-form finishes after connectedCallback)
        this.pwgApplyAllRules({ variant: initialVariant, html: null, serverDisabled: getDomServerDisabled() });
        requestAnimationFrame(() => {
          const v = this.getSelectedVariant(this);
          this.pwgApplyAllRules({ variant: v, html: null, serverDisabled: getDomServerDisabled() });
          this.pwgInitAccordions();
        });

        this.pwgInitAccordions();
        this.dispatchEvent(new CustomEvent('product-info:loaded', { bubbles: true }));
      }

      addPreProcessCallback(callback) {
        this.preProcessHtmlCallbacks.push(callback);
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.quantityForm = this.querySelector('.product-form__quantity');
        if (!this.quantityForm) return;

        this.setQuantityBoundries();

        if (!this.dataset.originalSection) {
          this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.fetchQuantityRules.bind(this));
        }
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber();
        this.cartUpdateUnsubscriber?.();
      }

      initializeProductSwapUtility() {
        this.preProcessHtmlCallbacks.push((html) =>
          html.querySelectorAll('.scroll-trigger').forEach((element) => element.classList.add('scroll-trigger--cancel'))
        );

        this.postProcessHtmlCallbacks.push((newNode) => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
        if (!this.contains(event.target)) return;

        this.resetProductFormState();

        const productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
        this.pendingRequestUrl = productUrl;
        const shouldSwapProduct = this.dataset.url !== productUrl;
        const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValues, shouldFetchFullPage),
          targetId: target.id,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage)
            : this.handleUpdateProductInfo(productUrl),
        });
      }

      resetProductFormState() {
        const productForm = this.productForm;
        productForm?.toggleSubmitButton(true);
        productForm?.handleErrorMessage();
      }

      handleSwapProduct(productUrl, updateFullPage) {
        return (html) => {
          this.productModal?.remove();

          const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info';
          const variant = this.getSelectedVariant(html.querySelector(selector));
          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML;

            HTMLUpdateUtility.viewTransition(
              document.querySelector('main'),
              html.querySelector('main'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector('product-info'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          }
        };
      }

      renderProductInfo({ requestUrl, targetId, callback }) {
        this.abortController?.abort();
        this.abortController = new AbortController();

        fetch(requestUrl, { signal: this.abortController.signal })
          .then((response) => response.text())
          .then((responseText) => {
            this.pendingRequestUrl = null;
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            callback(html);
          })
          .then(() => {
            document.querySelector(`#${targetId}`)?.focus();
          })
          .catch((error) => {
            if (error.name === 'AbortError') {
              console.log('Fetch aborted by user');
            } else {
              console.error(error);
            }
          });
      }

      getSelectedVariant(productInfoNode) {
        const selectedVariant = productInfoNode.querySelector('variant-selects [data-selected-variant]')?.innerHTML;
        return !!selectedVariant ? JSON.parse(selectedVariant) : null;
      }

      buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false) {
        const params = [];

        !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);

        if (optionValues.length) {
          params.push(`option_values=${optionValues.join(',')}`);
        }

        return `${url}?${params.join('&')}`;
      }

      updateOptionValues(html) {
        const variantSelects = html.querySelector('variant-selects');
        if (variantSelects) {
          HTMLUpdateUtility.viewTransition(this.variantSelectors, variantSelects, this.preProcessHtmlCallbacks);
        }
      }

      handleUpdateProductInfo(productUrl) {
        return (html) => {
          const variant = this.getSelectedVariant(html);

          this.pickupAvailability?.update(variant);
          this.updateOptionValues(html);
          this.updateURL(productUrl, variant?.id);
          this.updateVariantInputs(variant?.id);

          if (!variant) {
            this.setUnavailable();
            this.pwgApplyAllRules({ variant: null, html });
            return;
          }

          this.updateMedia(html, variant?.featured_media?.id);

          const updateSourceFromDestination = (id, shouldHide = (source) => false) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);
            const destination = this.querySelector(`#${id}-${this.dataset.section}`);
            if (source && destination) {
              destination.innerHTML = source.innerHTML;
              destination.classList.toggle('hidden', shouldHide(source));
            }
          };

          updateSourceFromDestination('price');
          updateSourceFromDestination('Sku', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Inventory', ({ innerText }) => innerText === '');
          updateSourceFromDestination('Volume');
          updateSourceFromDestination('Price-Per-Item', ({ classList }) => classList.contains('hidden'));

          this.updateQuantityRules(this.sectionId, html);
          this.querySelector(`#Quantity-Rules-${this.dataset.section}`)?.classList.remove('hidden');
          this.querySelector(`#Volume-Note-${this.dataset.section}`)?.classList.remove('hidden');

          const serverDisabled =
            html.getElementById(`ProductSubmitButton-${this.sectionId}`)?.hasAttribute('disabled') ?? true;

          // Dawn default button behavior
          this.productForm?.toggleSubmitButton(serverDisabled, window.variantStrings.soldOut);

          // Restore Dawn event for any listeners (apps, other theme assets)
          publish(PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant,
            },
          });

          this.pwgApplyAllRules({ variant, html, serverDisabled });
        };
      }

      updateVariantInputs(variantId) {
        this.querySelectorAll(
          `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`
        ).forEach((productForm) => {
          const input = productForm.querySelector('input[name="id"]');
          input.value = variantId ?? '';
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      }

      updateURL(url, variantId) {
        this.querySelector('share-button')?.updateUrl(
          `${window.shopUrl}${url}${variantId ? `?variant=${variantId}` : ''}`
        );

        if (this.dataset.updateUrl === 'false') return;
        window.history.replaceState({}, '', `${url}${variantId ? `?variant=${variantId}` : ''}`);
      }

      setUnavailable() {
        this.productForm?.toggleSubmitButton(true, window.variantStrings.unavailable);

        const selectors = ['price', 'Inventory', 'Sku', 'Price-Per-Item', 'Volume-Note', 'Volume', 'Quantity-Rules']
          .map((id) => `#${id}-${this.dataset.section}`)
          .join(', ');
        document.querySelectorAll(selectors).forEach(({ classList }) => classList.add('hidden'));
      }

      updateMedia(html, variantFeaturedMediaId) {
        if (!variantFeaturedMediaId) return;

        const mediaGallerySource = this.querySelector('media-gallery ul');
        const mediaGalleryDestination = html.querySelector(`media-gallery ul`);

        const refreshSourceData = () => {
          if (this.hasAttribute('data-zoom-on-hover')) enableZoomOnHover(2);
          const mediaGallerySourceItems = Array.from(mediaGallerySource.querySelectorAll('li[data-media-id]'));
          const sourceSet = new Set(mediaGallerySourceItems.map((item) => item.dataset.mediaId));
          const sourceMap = new Map(
            mediaGallerySourceItems.map((item, index) => [item.dataset.mediaId, { item, index }])
          );
          return [mediaGallerySourceItems, sourceSet, sourceMap];
        };

        if (mediaGallerySource && mediaGalleryDestination) {
          let [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
          const mediaGalleryDestinationItems = Array.from(
            mediaGalleryDestination.querySelectorAll('li[data-media-id]')
          );
          const destinationSet = new Set(mediaGalleryDestinationItems.map(({ dataset }) => dataset.mediaId));
          let shouldRefresh = false;

          for (let i = mediaGalleryDestinationItems.length - 1; i >= 0; i--) {
            if (!sourceSet.has(mediaGalleryDestinationItems[i].dataset.mediaId)) {
              mediaGallerySource.prepend(mediaGalleryDestinationItems[i]);
              shouldRefresh = true;
            }
          }

          for (let i = 0; i < mediaGallerySourceItems.length; i++) {
            if (!destinationSet.has(mediaGallerySourceItems[i].dataset.mediaId)) {
              mediaGallerySourceItems[i].remove();
              shouldRefresh = true;
            }
          }

          if (shouldRefresh) [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();

          mediaGalleryDestinationItems.forEach((destinationItem, destinationIndex) => {
            const sourceData = sourceMap.get(destinationItem.dataset.mediaId);

            if (sourceData && sourceData.index !== destinationIndex) {
              mediaGallerySource.insertBefore(
                sourceData.item,
                mediaGallerySource.querySelector(`li:nth-of-type(${destinationIndex + 1})`)
              );

              [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
            }
          });
        }

        this.querySelector(`media-gallery`)?.setActiveMedia?.(`${this.dataset.section}-${variantFeaturedMediaId}`, true);

        const modalContent = this.productModal?.querySelector(`.product-media-modal__content`);
        const newModalContent = html.querySelector(`product-modal .product-media-modal__content`);

        if (modalContent && newModalContent) {
          modalContent.innerHTML = newModalContent.innerHTML;
        }
      }

      setQuantityBoundries() {
        const data = {
          cartQuantity: this.quantityInput.dataset.cartQuantity ? parseInt(this.quantityInput.dataset.cartQuantity) : 0,
          min: this.quantityInput.dataset.min ? parseInt(this.quantityInput.dataset.min) : 1,
          max: this.quantityInput.dataset.max ? parseInt(this.quantityInput.dataset.max) : null,
          step: this.quantityInput.step ? parseInt(this.quantityInput.step) : 1,
        };

        let min = data.min;
        const max = data.max === null ? data.max : data.max - data.cartQuantity;
        if (max !== null) min = Math.min(min, max);
        if (data.cartQuantity >= data.min) min = Math.min(min, data.step);

        this.quantityInput.min = min;

        if (max) {
          this.quantityInput.max = max;
        } else {
          this.quantityInput.removeAttribute('max');
        }
        this.quantityInput.value = min;

        publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.variantIdInput?.value;
        if (!currentVariantId) return;

        this.querySelector('.quantity__rules-cart .loading__spinner').classList.remove('hidden');
        return fetch(`${this.dataset.url}?variant=${currentVariantId}&section_id=${this.dataset.section}`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            this.updateQuantityRules(this.sectionId, html);
          })
          .catch((e) => console.error(e))
          .finally(() => this.querySelector('.quantity__rules-cart .loading__spinner').classList.add('hidden'));
      }

      updateQuantityRules(sectionId, html) {
        if (!this.quantityInput) return;
        this.setQuantityBoundries();

        const quantityFormUpdated = html.getElementById(`Quantity-Form-${sectionId}`);
        const selectors = ['.quantity__input', '.quantity__rules', '.quantity__label'];

        for (let selector of selectors) {
          const current = this.quantityForm.querySelector(selector);
          const updated = quantityFormUpdated?.querySelector(selector);
          if (!current || !updated) continue;

          if (selector === '.quantity__input') {
            const attributes = ['data-cart-quantity', 'data-min', 'data-max', 'step'];

            for (let attribute of attributes) {
              const valueUpdated = updated.getAttribute(attribute);
              if (valueUpdated !== null) {
                current.setAttribute(attribute, valueUpdated);
              } else {
                current.removeAttribute(attribute);
              }
            }
          } else {
            current.innerHTML = updated.innerHTML;

            if (selector === '.quantity__label') {
              const updatedAriaLabelledBy = updated.getAttribute('aria-labelledby');
              if (updatedAriaLabelledBy) {
                current.setAttribute('aria-labelledby', updatedAriaLabelledBy);

                const labelId = updatedAriaLabelledBy;
                const currentHiddenLabel = document.getElementById(labelId);
                const updatedHiddenLabel = html.getElementById(labelId);

                if (currentHiddenLabel && updatedHiddenLabel) {
                  currentHiddenLabel.textContent = updatedHiddenLabel.textContent;
                }
              }
            }
          }
        }
      }

      get productForm() {
        return this.querySelector('product-form');
      }

      get productModal() {
        return document.querySelector(`#ProductModal-${this.dataset.section}`);
      }

      get pickupAvailability() {
        return this.querySelector('pickup-availability');
      }

      get variantSelectors() {
        return this.querySelector('variant-selects');
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }

      pwgNormalize(v) {
        return (v || '').toString().trim().toLowerCase();
      }

      pwgGetSelectedValueByOptionName(optionName) {
        const vs = this.variantSelectors;
        if (!vs) return '';

        const normalizedOptionName = this.pwgNormalize(optionName);

        const select = Array.from(vs.querySelectorAll('select[name^="options["]')).find((element) => {
          const match = (element.name || '').match(/^options\[(.*)\]$/);
          return match && this.pwgNormalize(match[1]) === normalizedOptionName;
        });

        if (select) return (select.value || '').toString();

        const radio = Array.from(vs.querySelectorAll('input[type="radio"]:checked')).find((element) => {
          const inputName = (element.name || '').replace(/-\d+$/, '');
          return this.pwgNormalize(inputName) === normalizedOptionName;
        });

        return radio ? (radio.value || '').toString() : '';
      }

      pwgShouldShowNameplate(variant) {
        if (!variant) return false;

        const nameplateValue = this.pwgGetSelectedValueByOptionName(PWG_NAMEPLATE_OPTION_NAME);
        if (nameplateValue) {
          return this.pwgNormalize(nameplateValue) === this.pwgNormalize(PWG_NAMEPLATE_YES_VALUE);
        }

        return [variant.option1, variant.option2, variant.option3].some((value) =>
          this.pwgNormalize(value).includes(this.pwgNormalize(PWG_NAMEPLATE_LABEL_FRAGMENT))
        );
      }

      pwgSetNameplateInputsEnabled(container, enabled) {
        container.querySelectorAll('input, textarea, select').forEach((element) => {
          element.disabled = !enabled;
        });
      }

      pwgUpdateNameplateFields(variant) {
        const fields = this.querySelectorAll('.nameplate-text');
        if (!fields.length) return;

        const show = this.pwgShouldShowNameplate(variant);
        fields.forEach((el) => {
          this.pwgSetNameplateInputsEnabled(el, show);

          const animateOpen = show && el.dataset.pwgSlideInit === '1';
          window.PWGSlideToggle.setExpanded(el, show, { animate: animateOpen, duration: 220 });
          el.dataset.pwgSlideInit = '1';
        });
      }

      pwgInitAccordions() {
        const detailsNodes = this.querySelectorAll('.product__accordion details');
        if (!detailsNodes.length) return;

        detailsNodes.forEach((details) => {
          if (details.dataset.pwgAccordionBound === '1') {
            const content = details.querySelector('.accordion__content');
            if (content) {
              window.PWGSlideToggle.setExpanded(content, details.open, { animate: false });
            }
            return;
          }

          details.dataset.pwgAccordionBound = '1';

          const summary = details.querySelector('summary');
          const content = details.querySelector('.accordion__content');
          if (!summary || !content) return;

          window.PWGSlideToggle.setExpanded(content, details.open, { animate: false });

          summary.addEventListener('click', (event) => {
            event.preventDefault();

            if (details.dataset.pwgAccordionAnimating === '1') return;
            details.dataset.pwgAccordionAnimating = '1';

            const shouldOpen = !details.open;

            if (shouldOpen) {
              details.open = true;
            }

            window.PWGSlideToggle.setExpanded(content, shouldOpen, {
              animate: true,
              duration: 220,
              onComplete: () => {
                if (!shouldOpen) details.open = false;
                details.dataset.pwgAccordionAnimating = '0';
              },
            });
          });
        });
      }

      pwgHasInvalidPlaceholder(variant) {
        if (!variant || !variant.option2) return false;
        return this.pwgNormalize(variant.option2) === this.pwgNormalize(PWG_INVALID_OPTION_LABEL);
      }

      pwgSetDynamicCheckoutDisabled(disabled) {
        this.classList.toggle('pwg-invalid-selection', !!disabled);
      }

      pwgSetPriceMismatchMessage() {
        const priceEl = this.querySelector(`#price-${this.dataset.section}`);
        if (!priceEl) return;

        priceEl.innerHTML = `<span class="pwg-price-mismatch">${PWG_PRICE_MISMATCH_TEXT}</span>`;
        priceEl.classList.remove('hidden');
      }

      pwgSetSubmitDisabled(disabled, text = null) {
        if (this.productForm?.toggleSubmitButton) {
          if (disabled && typeof text === 'string') this.productForm.toggleSubmitButton(true, text);
          else if (disabled) this.productForm.toggleSubmitButton(true);
          else this.productForm.toggleSubmitButton(false);
        }

        const btn =
          this.querySelector('.product-form__submit') || this.querySelector(`#ProductSubmitButton-${this.sectionId}`);
        if (!btn) return;

        const span = btn.querySelector('span');
        if (!btn.dataset.pwgOriginalText && span) btn.dataset.pwgOriginalText = span.textContent.trim();

        if (disabled) {
          btn.setAttribute('disabled', 'disabled');
          btn.setAttribute('aria-disabled', 'true');
          if (span && typeof text === 'string') span.textContent = text;
        } else {
          btn.removeAttribute('disabled');
          btn.removeAttribute('aria-disabled');
          if (span && btn.dataset.pwgOriginalText) span.textContent = btn.dataset.pwgOriginalText;
        }
      }

      pwgGetSelectedValueByPosition(position) {
        const vs = this.variantSelectors;
        if (!vs) return '';

        const select = vs.querySelector(`select#Option-${vs.dataset.section}-${position - 1}`);
        if (select) return (select.value || '').toString();

        const radio = vs.querySelector(`input[type="radio"][name$="-${position}"]:checked`);
        if (radio) return (radio.value || '').toString();

        return '';
      }

      pwgExtractSizeToken(text) {
        const t = (text || '').toString().toLowerCase();
        if (t.includes('5x7') || t.includes('5 x 7')) return '5x7';
        if (t.includes('8x10') || t.includes('8 x 10')) return '8x10';
        return '';
      }

      pwgIsSizeFrameMismatchFromSelections() {
        const opt2 = this.pwgGetSelectedValueByPosition(2);
        const opt3 = this.pwgGetSelectedValueByPosition(3);

        if (!opt2 || !opt3) return false;
        if (this.pwgNormalize(opt2) === this.pwgNormalize(PWG_INVALID_OPTION_LABEL)) return false;
        if (this.pwgNormalize(opt3) === this.pwgNormalize(PWG_INVALID_OPTION_LABEL)) return false;

        const s2 = this.pwgExtractSizeToken(opt2);
        const s3 = this.pwgExtractSizeToken(opt3);

        if (!s2 || !s3) return false;

        return (s2 === '5x7' && s3 === '8x10') || (s2 === '8x10' && s3 === '5x7');
      }

      pwgApplyAllRules({ variant, html = null, serverDisabled = null }) {
        this.pwgUpdateNameplateFields(variant);

        const mismatch = this.pwgIsSizeFrameMismatchFromSelections();
        if (mismatch) {
          this.pwgSetDynamicCheckoutDisabled(true);
          this.pwgSetSubmitDisabled(true);
          this.pwgSetPriceMismatchMessage();
          return;
        }

        const invalidPlaceholder = this.pwgHasInvalidPlaceholder(variant);
        if (invalidPlaceholder) {
          this.pwgSetDynamicCheckoutDisabled(true);
          this.pwgSetSubmitDisabled(true, PWG_MAKE_SELECTION_BUTTON_TEXT);
          return;
        }

        this.pwgSetDynamicCheckoutDisabled(false);

        if (typeof serverDisabled === 'boolean') {
          this.pwgSetSubmitDisabled(serverDisabled);
        } else if (variant) {
          // If variant exists but we don't know the server state, respect the current DOM state.
          const btn =
            this.querySelector('.product-form__submit') || this.querySelector(`#ProductSubmitButton-${this.sectionId}`);
          const domDisabled = btn ? btn.hasAttribute('disabled') : false;
          this.pwgSetSubmitDisabled(domDisabled);
        }
      }
    }
  );
}
